import React, { useMemo } from 'react';
import classNames from 'classnames';
import { round, isNumber } from 'lodash';
import { Typography, Box, Stack, useTheme } from '@mui/material';
import { WindArrowIcon } from '@wavetrak/theme';
import { mapDegreesCardinal } from '@surfline/web-common';
import type { Speed } from 'types/units';
import type { DirectionType } from 'types/wind';
import { convertKnotsToMph, convertKphToMph } from 'utils/convertUnits';
import TableCellTooltip from '../TableCellTooltip';

import styles from './WindCell.module.scss';

const cx = classNames.bind(styles);

type Props = {
  isDataObserved?: boolean;
  loading?: boolean;
  directionType: DirectionType | undefined;
  direction: number | null;
  speed: number | null | undefined;
  gust: number | null | undefined;
  units: Speed | string | undefined;
};

const WindCell: React.FC<Props> = ({
  isDataObserved = false,
  direction,
  directionType,
  speed,
  gust,
  units,
  loading,
}) => {
  const error = !isNumber(direction) || !isNumber(speed) || !isNumber(gust);
  const loadingOrError = loading || error;
  const theme = useTheme();

  const rotation = useMemo(
    () => (loadingOrError ? 0 - 180 : direction - 180),
    [loadingOrError, direction],
  );

  const threshold = useMemo(() => {
    if (units?.toLowerCase() === 'kts') return 8.6;
    if (units?.toLowerCase() === 'kph') return 16;
    return 10;
  }, [units]);

  const gustColor = useMemo(() => {
    if (loadingOrError) return 'text.light';

    return gust > speed * 1.5 && gust > threshold ? 'error.main' : 'text.primary';
  }, [gust, loadingOrError, speed, threshold]);

  const arrowLoading = useMemo(() => (loadingOrError ? styles.arrowLoading : ''), [loadingOrError]);
  const arrowColor = useMemo(
    () => (loadingOrError ? theme.palette.text.light : theme.palette.text.brandSecondary),
    [loadingOrError, theme.palette.text.brandSecondary, theme.palette.text.light],
  );

  const arrowStyles = useMemo(() => {
    let opacity = 0;
    if (loadingOrError) {
      return { backgroundColor: theme.palette.text.light };
    }
    let windSpeed = round(speed, 0);
    // always use the MPH equivalent for determining the opacity
    if (units?.toLowerCase() === 'kph') windSpeed = round(convertKphToMph(speed || 0), 0);
    if (units?.toLowerCase() === 'kts') windSpeed = round(convertKnotsToMph(speed || 0), 0);
    if (windSpeed >= 0 && windSpeed < 1) opacity = 0.1;
    if (windSpeed >= 1 && windSpeed <= 3) opacity = 0.15;
    if (windSpeed >= 4 && windSpeed <= 7) opacity = 0.2;
    if (windSpeed >= 8 && windSpeed <= 12) opacity = 0.25;
    if (windSpeed >= 13 && windSpeed <= 18) opacity = 0.3;
    if (windSpeed >= 19 && windSpeed <= 24) opacity = 0.35;
    if (windSpeed >= 25 && windSpeed <= 31) opacity = 0.4;
    if (windSpeed >= 32 && windSpeed <= 38) opacity = 0.45;
    if (windSpeed >= 39 && windSpeed <= 46) opacity = 0.5;
    if (windSpeed >= 47 && windSpeed <= 54) opacity = 0.55;
    if (windSpeed >= 55 && windSpeed <= 63) opacity = 0.6;
    if (windSpeed >= 64 && windSpeed <= 72) opacity = 0.65;
    if (windSpeed >= 73) opacity = 0.7;

    return {
      // This is the rgba equivalent of forecast primary (#b9d3df) from the theme
      backgroundColor: `rgba(185, 211, 223, ${opacity})`,
    };
  }, [loadingOrError, speed, theme.palette.text.light, units]);

  const tooltipTitle = useMemo(
    () => (
      <Typography component="span" data-testid="forecast-table-wind-tooltip">
        <Typography color="white" component="h3" variant="caption1">
          {directionType} wind
        </Typography>
        <Typography
          color="white"
          component="p"
          variant="callout2"
          style={{
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          {mapDegreesCardinal(direction)}, {round(direction || 0, 0)}º
        </Typography>
      </Typography>
    ),
    [direction, directionType],
  );
  return (
    <Stack
      className={classNames({
        [styles.wrapper]: true,
        [styles.observed]: isDataObserved,
      })}
      data-testid="forecast-table-wind-cell"
    >
      <Box className={styles.content} data-testid="forecast-table-wind-content">
        {loadingOrError ? (
          <Typography
            className={styles.empty}
            color="text.light"
            data-testid="forecast-table-wind-empty"
            variant="caption2"
          >
            --{units?.toLowerCase()}
          </Typography>
        ) : (
          <Box className={styles.details} data-testid="forecast-table-wind-details">
            <Typography
              className={styles.speed}
              color="text.primary"
              data-testid="forecast-table-wind-speed"
              variant="headline"
            >
              {round(speed, 0)}
            </Typography>
            <Stack className={styles.gustWrapper}>
              <Typography
                className={styles.gust}
                color={gustColor}
                data-testid="forecast-table-wind-gust"
                variant="caption2"
              >
                {round(gust, 0)}
              </Typography>
              <Typography
                className={styles.units}
                color="text.primary"
                data-testid="forecast-table-wind-units"
                variant="caption2"
              >
                {units?.toLowerCase()}
              </Typography>
            </Stack>
          </Box>
        )}
        {error && !loading ? null : (
          <TableCellTooltip title={tooltipTitle} loading={loadingOrError}>
            <Box
              className={cx([styles.arrowWrapper, arrowLoading])}
              data-testid="forecast-table-wind-arrow"
              style={arrowStyles}
            >
              <WindArrowIcon
                className={styles.arrow}
                color={arrowColor}
                rotation={rotation}
                size={20}
              />
            </Box>
          </TableCellTooltip>
        )}
      </Box>
    </Stack>
  );
};

export default WindCell;
